import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import React from "react";
import { useEffect, useState } from "react";
import { useMediaQuery } from "usehooks-ts";

type Option = {
  onClick: () => void;
  icon: React.ReactElement;
  value: string;
  label?: React.ReactElement | string;
  chosen?: boolean;
  disabled?: boolean;
};

type Props = {
  options: Option[];
  grayScale?: boolean;
  activeValue?: string;
  optionCanBeDeselected?: boolean;
  expandedVersion?: boolean;
  disabled?: boolean;
};

const SegmentedControls = ({
  options,
  grayScale,
  activeValue,
  optionCanBeDeselected,
  disabled,
}: Props) => {
  const findIndex = options.findIndex((o) => o.value === activeValue);
  const [active, setActive] = useState(findIndex === -1 ? NaN : findIndex);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);

  useEffect(() => {
    if (activeValue === "none") {
      setActive(NaN);
    } else if (!optionCanBeDeselected) {
      setActive(options.findIndex((o) => o.value === activeValue));
    }
  }, [activeValue, setActive, optionCanBeDeselected, options]);

  const handleOptionClick = (index: number) => {
    if (optionCanBeDeselected && index === active) {
      setActive(NaN);
    } else {
      setActive(index);
    }
    options[index].onClick();
  };

  return (
    <div className="flex flex-row desktop:justify-center items-center text-white w-full gap-1">
      <div
        className={cn(
          "w-full flex justify-start text-xs mr-1",
          disabled ? "text-dark-disabled" : "text-dark-tertiary",
        )}
      >
        {options[active]?.label}
      </div>
      {options.map((option, index) => (
        <div className="flex flex-row" key={index}>
          <button
            disabled={option.disabled}
            onClick={(e) => {
              e.stopPropagation();
              handleOptionClick(index);
            }}
            className={cn(
              "h-8 w-11 rounded-sm flex items-center justify-center",
              isMobile && "w-14.5 h-9",
              disabled
                ? index === active
                  ? "bg-action-tertiary"
                  : "bg-panel-background"
                : index === active
                  ? "bg-action-tertiary-active"
                  : "bg-action-tertiary hover:bg-action-tertiary-hover",
            )}
          >
            <div
              className={cn(
                "w-5 h-5",
                isMobile && "w-6 h-6",
                disabled
                  ? index === active
                    ? "text-dark-tertiary"
                    : "text-dark-disabled"
                  : index !== active &&
                      !option.chosen &&
                      (grayScale ? "grayscale opacity-30" : "opacity-40"),
              )}
            >
              {option.icon}
            </div>
          </button>
        </div>
      ))}
    </div>
  );
};

export default SegmentedControls;
